import { useState } from 'react';
import { Plus, Pencil, XCircle } from 'lucide-react';
import { Button, Badge, Modal, Input, EmptyState } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
} from '../../../hooks/useFinancialOps';

const TYPE_LABELS: Record<string, string> = {
  INCOME: 'Receita',
  EXPENSE: 'Despesa',
};

const DRE_GROUP_LABELS: Record<string, string> = {
  RECEITA_BRUTA: 'Receita Bruta',
  PASS_THROUGH: 'Pass-through (Terceiros)',
  DESPESA_OPERACIONAL: 'Despesa Operacional',
  NAO_OPERACIONAL: 'Não-operacional',
};

function defaultDreGroup(type: string) {
  return type === 'INCOME' ? 'RECEITA_BRUTA' : 'DESPESA_OPERACIONAL';
}

interface CategoryFormData {
  name: string;
  type: string;
  color: string;
  dreGroup: string;
}

const emptyForm: CategoryFormData = {
  name: '',
  type: 'EXPENSE',
  color: '#94A3B8',
  dreGroup: 'DESPESA_OPERACIONAL',
};

export function CategoriasPage() {
  const { data, isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryFormData>(emptyForm);

  const categories = data?.data ?? [];

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  }

  function openEdit(category: any) {
    setEditingId(category.id);
    setForm({
      name: category.name ?? '',
      type: category.type ?? 'EXPENSE',
      color: category.color ?? '#94A3B8',
      dreGroup: category.dreGroup ?? defaultDreGroup(category.type ?? 'EXPENSE'),
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingId) {
      await updateCategory.mutateAsync({ id: editingId, data: form });
    } else {
      await createCategory.mutateAsync(form);
    }
    setShowModal(false);
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-ink">Categorias Financeiras</h2>
        <Button onClick={openCreate} size="sm">
          <Plus size={16} className="mr-1" /> Nova Categoria
        </Button>
      </div>

      {categories.length === 0 ? (
        <EmptyState
          title="Nenhuma categoria cadastrada"
          body="Categorias servem para classificar receitas e despesas (ex.: Vendas, Aluguel, Marketing)."
        />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Cor</th>
                <th className="px-4 py-2 text-left font-medium">Nome</th>
                <th className="px-4 py-2 text-left font-medium">Tipo</th>
                <th className="px-4 py-2 text-left font-medium">Grupo DRE</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat: any) => (
                <tr key={cat.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="px-4 py-3">
                    <div
                      className="h-5 w-5 rounded-full border border-border"
                      style={{ backgroundColor: cat.color }}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-ink">{cat.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={cat.type === 'INCOME' ? 'accent' : 'danger'}>
                      {TYPE_LABELS[cat.type] ?? cat.type}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {DRE_GROUP_LABELS[cat.dreGroup ?? defaultDreGroup(cat.type)] ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={cat.active ? 'accent' : 'neutral'}>
                      {cat.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(cat)}
                        className="text-ink-secondary hover:text-ink-primary"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      {cat.active && (
                        <button
                          onClick={() =>
                            updateCategory.mutate({ id: cat.id, data: { active: false } })
                          }
                          className="text-ink-secondary hover:text-red-500"
                          title="Desativar"
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Categoria' : 'Nova Categoria'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Tipo *
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  type: e.target.value,
                  dreGroup: defaultDreGroup(e.target.value),
                }))
              }
              disabled={!!editingId}
            >
              <option value="INCOME">Receita</option>
              <option value="EXPENSE">Despesa</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Grupo DRE
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={form.dreGroup}
              onChange={(e) => setForm((f) => ({ ...f, dreGroup: e.target.value }))}
            >
              {Object.entries(DRE_GROUP_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink-tertiary mt-1">
              Define em qual seção da DRE as transações desta categoria aparecem.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Cor
            </label>
            <input
              type="color"
              className="h-9 w-20 border border-border rounded-md bg-surface"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" loading={createCategory.isPending || updateCategory.isPending}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
